import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { LogOut, Menu, X, Sun, Moon } from "lucide-react";
import { api, auth } from "../lib/api";
import { useSettings } from "../lib/settings";
import { AgencyLink as Link } from "../lib/agency";
import { getEffectiveTheme, setTheme } from "../lib/theme";

export default function Layout({ children, showNav = true }) {
  const loc = useLocation();
  const navigate = useNavigate();
  const user = auth.current();
  const { settings } = useSettings();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [theme, setThemeState] = useState(() => getEffectiveTheme());
  const [hasUnreadChats, setHasUnreadChats] = useState(false);
  const isStaff = user && ["master", "agency_admin"].includes(user.role);

  useEffect(() => {
    const onStorage = () => setThemeState(getEffectiveTheme());
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // Antes solo se veía un punto rojo dentro de la tarjeta de Chats en
  // /admin — si el admin estaba en cualquier otra pantalla, no se enteraba
  // de un mensaje nuevo salvo que entrara ahí a propósito.
  useEffect(() => {
    if (user?.role !== "agency_admin") return;
    let cancelled = false;
    const poll = () => {
      api.get("/admin/unread-chats")
        .then(({ data }) => { if (!cancelled) setHasUnreadChats((data.unread_booking_ids || []).length > 0); })
        .catch(() => {});
    };
    poll();
    const id = setInterval(poll, 15000);
    return () => { cancelled = true; clearInterval(id); };
  }, [user?.role]);

  const toggleTheme = () => {
    const next = theme === "light" ? "dark" : "light";
    setTheme(next);
    setThemeState(next);
  };

  // El staff de agencia no tiene itinerario propio — no le sirven los links
  // de viajero, así que ve solo su panel. El master no opera ninguna agencia
  // en particular — su nav es distinto: solo dar de alta agencias y ver
  // reportes agregados, nada del día a día de una agencia específica.
  const navItems = user?.role === "master"
    ? [
        { to: "/master", label: "Agencias" },
        { to: "/master/reportes", label: "Reportes" },
        { to: "/master/codigos", label: "Códigos" },
        { to: "/master/personalizacion", label: "Personalización" },
      ]
    : isStaff
    ? [
        { to: "/admin", label: "Admin" },
        { to: "/admin/settings", label: "Marca" },
        { to: "/admin/plan", label: "Mi Plan" },
      ]
    : user
    ? [
        { to: "/dashboard", label: "Itinerario" },
        { to: "/chat", label: "Chat" },
        { to: "/tools", label: "Herramientas" },
        { to: "/feedback", label: "Feedback" },
      ]
    : [
        { to: "/promociones", label: "Promociones" },
        { to: "/groups", label: "Grupos SOS" },
        { to: "/login", label: "Acceso" },
      ];

  // Quita el prefijo /a/:slug para comparar contra las rutas "desnudas" de navItems
  const agencySlugMatch = loc.pathname.match(/^(\/a\/[^/]+)(\/.*)?$/);
  const agencyBasePath = agencySlugMatch ? agencySlugMatch[1] : "";
  const barePathname = agencySlugMatch ? (agencySlugMatch[2] || "/") : loc.pathname;

  const onLogout = () => { auth.logout(); navigate(agencyBasePath || "/"); };

  // Split agency name for cyan accent
  const parts = (settings.agency_name || "Travel Shield").split(" ");
  const first = parts[0];
  const rest = parts.slice(1).join(" ");

  return (
    <div className="min-h-screen bg-black text-neutral-100 bg-grain relative">
      {showNav && (
        <header className="fixed top-0 inset-x-0 z-40 glass" data-testid="main-header">
          <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2.5 group" data-testid="brand-link">
              <img
                src={settings.logo_url || "/logo192.png"}
                alt={settings.agency_name}
                className="h-7 w-auto group-hover:scale-110 transition-transform"
              />
              <span className="font-display text-lg tracking-[0.25em] uppercase">
                {first}{rest && <span className="text-[var(--brand-cyan,#FF85AC)]">·</span>}{rest}
              </span>
            </Link>
            <nav className="hidden md:flex items-center gap-7">
              {navItems.map((n) => (
                <Link
                  key={n.to}
                  to={n.to}
                  data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`relative text-xs uppercase tracking-[0.2em] transition-colors ${
                    barePathname === n.to || (!["/admin", "/master"].includes(n.to) && barePathname.startsWith(n.to))
                      ? "text-[var(--brand-cyan,#FF85AC)]"
                      : "text-neutral-400 hover:text-white"
                  }`}
                >
                  {n.label}
                  {n.to === "/admin" && hasUnreadChats && (
                    <span
                      data-testid="nav-admin-unread-dot"
                      className="absolute -top-1.5 -right-2.5 w-2 h-2 rounded-full bg-[#FF3B30]"
                    />
                  )}
                </Link>
              ))}
            </nav>
            <div className="flex items-center gap-3">
              <button
                onClick={toggleTheme}
                className="text-neutral-400 hover:text-[var(--brand-cyan,#FF85AC)] w-11 h-11 inline-flex items-center justify-center rounded-full transition-colors"
                data-testid="theme-toggle"
                aria-label={theme === "light" ? "Cambiar a modo oscuro" : "Cambiar a modo claro"}
                title={theme === "light" ? "Modo oscuro" : "Modo claro"}
              >
                {theme === "light" ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
              </button>
              {user && (
                <button
                  onClick={onLogout}
                  className="hidden md:flex text-xs uppercase tracking-[0.2em] text-neutral-400 hover:text-[var(--brand-cyan,#FF85AC)] items-center gap-1.5"
                  data-testid="logout-btn"
                >
                  <LogOut className="w-3.5 h-3.5" /> Salir
                </button>
              )}
              <button
                onClick={() => setMobileOpen((v) => !v)}
                className="md:hidden text-neutral-300 hover:text-[var(--brand-cyan,#FF85AC)] w-11 h-11 inline-flex items-center justify-center"
                data-testid="mobile-menu-toggle"
                aria-label="Menú"
              >
                {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>
          {mobileOpen && (
            <nav className="md:hidden border-t border-white/10 px-6 py-4 flex flex-col gap-1" data-testid="mobile-menu">
              {navItems.map((n) => (
                <Link
                  key={n.to}
                  to={n.to}
                  onClick={() => setMobileOpen(false)}
                  data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`flex items-center gap-2 text-sm uppercase tracking-[0.2em] py-3 border-b border-white/5 last:border-0 transition-colors ${
                    barePathname === n.to || (!["/admin", "/master"].includes(n.to) && barePathname.startsWith(n.to))
                      ? "text-[var(--brand-cyan,#FF85AC)]"
                      : "text-neutral-300"
                  }`}
                >
                  {n.label}
                  {n.to === "/admin" && hasUnreadChats && (
                    <span data-testid="mobile-nav-admin-unread-dot" className="w-2 h-2 rounded-full bg-[#FF3B30]" />
                  )}
                </Link>
              ))}
              {user && (
                <button
                  onClick={() => { setMobileOpen(false); onLogout(); }}
                  className="text-sm uppercase tracking-[0.2em] text-neutral-400 py-3 text-left flex items-center gap-2"
                  data-testid="mobile-logout-btn"
                >
                  <LogOut className="w-4 h-4" /> Salir
                </button>
              )}
            </nav>
          )}
        </header>
      )}
      <main className={`relative z-10 ${showNav ? "pt-16" : ""}`}>{children}</main>
    </div>
  );
}
